// project imports
// action - state management
import {
  CUMULATIVE_INSTALLS_REDUCER_KEY,
  CUMULATIVE_MEMBERS_REDUCER_KEY,
  SUBSCRIBER_ACQUISITION_ASSUMPTIONS_REDUCER_KEY,
} from "..";
import * as actionTypes from "../../actions/actionTypes";
import { INSTALL_KEY } from "./cumulativeInstallsReducer";
import { itemType } from "../types";
import { years } from "store/utils/years";
import {
  cumulativeMembersMonths,
  registrationMonths,
  totalMonths,
} from "./staticYearsData";

// This is to filter based on company types
import { SaaSCoType, GeneralCoType } from "constants/companyTypes";

// subscriber acquisition assumption' keys
export const CUMULATIVE_MEMBERS_KEY = "cumulativeMembers";
export const REGISTRATION_KEY = "registration";
export const DEREGISTRATION_KEY = "deregistration";
export const TOTAL_KEY = "total";
export const REGISTRATION_INSTALL_TO_MEMBER_KEY = "registrationInstallToMember";
export const CHURN_KEY = "churn";
export const DEREGISTRATION_PERCENTAGE_KEY = "deregistrationPercentage";

export const items = [
  CUMULATIVE_MEMBERS_KEY,
  REGISTRATION_KEY,
  DEREGISTRATION_KEY,
  TOTAL_KEY,
  REGISTRATION_INSTALL_TO_MEMBER_KEY,
  CHURN_KEY,
  DEREGISTRATION_PERCENTAGE_KEY,
];

export const initialState = {
  [CUMULATIVE_MEMBERS_KEY]: {
    title: "Cumulative Members",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (yearKey === 1) {
        return getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)[TOTAL_KEY][
          "initialPeriod"
        ].toFixed(0);
      }
      return getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)
        [TOTAL_KEY].calculate(yearKey - 1, getReducerFunc)
        .toFixed(0);
    },
    months: cumulativeMembersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [REGISTRATION_KEY]: {
    title: "Registration",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        (getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[INSTALL_KEY].calculate(
          yearKey,
          getReducerFunc
        ) *
          getReducerFunc(SUBSCRIBER_ACQUISITION_ASSUMPTIONS_REDUCER_KEY)[
            "registration"
          ]["years"][yearKey]) /
        100;
      return result.toFixed(0);
    },
    months: registrationMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [DEREGISTRATION_KEY]: {
    title: "Deregistration",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const deregistrationPercentage = getReducerFunc(
        CUMULATIVE_MEMBERS_REDUCER_KEY
      )[DEREGISTRATION_PERCENTAGE_KEY].calculate(
        yearKey,
        getReducerFunc,
        companyType
      );
      return deregistrationPercentage === 0
        ? 0
        : (deregistrationPercentage * -1).toFixed(0);
    },
    months: cumulativeMembersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [TOTAL_KEY]: {
    title: "Total",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    showInitialPeriod: true,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let previousTotal = getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)[
        TOTAL_KEY
      ]["initialPeriod"];

      if (yearKey > 1) {
        previousTotal = getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)[
          TOTAL_KEY
        ].calculate(yearKey - 1, getReducerFunc);
      }

      const registration = getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)[
        REGISTRATION_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const deregistration = getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)[
        DEREGISTRATION_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      return (
        Number(previousTotal) + Number(registration) + Number(deregistration)
      );
    },
    months: totalMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [REGISTRATION_INSTALL_TO_MEMBER_KEY]: {
    title: "Registration (install to member)",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return getReducerFunc(SUBSCRIBER_ACQUISITION_ASSUMPTIONS_REDUCER_KEY)[
        "registration"
      ]["years"][yearKey];
    },
    months: cumulativeMembersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [CHURN_KEY]: {
    title: "Churn from previous year",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: cumulativeMembersMonths,
    years: {
      [years.YEAR_1_KEY]: 10,
      [years.YEAR_2_KEY]: 10,
      [years.YEAR_3_KEY]: 10,
      [years.YEAR_4_KEY]: 10,
      [years.YEAR_5_KEY]: 10,
    },
  },
  [DEREGISTRATION_PERCENTAGE_KEY]: {
    title: "Deregistration",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let total = getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)[TOTAL_KEY][
        "initialPeriod"
      ];

      if (yearKey > 1) {
        total = getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)[
          TOTAL_KEY
        ].calculate(yearKey - 1, getReducerFunc);
      }

      const result =
        (getReducerFunc(CUMULATIVE_MEMBERS_REDUCER_KEY)[CHURN_KEY]["years"][
          yearKey
        ] /
          100) *
        total;
      return result.toFixed(0);
    },
    months: cumulativeMembersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

// ==============================|| SUBSCRIPTION ACQUISITION ASSUMPTIONS REDUCER ||============================== //

const cumulativeMembersReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_CUMULATIVE_MEMBER_BY_YEAR:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          years: {
            ...state[action.key].years,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.SET_CUMULATIVE_MEMBER_BY_MONTHS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: {
            ...state[action.key].months,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.REPLACE_CUMULATIVE_MEMBER_YEARS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: action?.payload,
        },
      };
    case actionTypes.RESET_CUMULATIVE_MEMBER_REDUCER:
      return initialState;
    default:
      return state;
  }
};

export default cumulativeMembersReducer;
